import { parseAmount, USD } from "@loot-os/api-contract";
import type { LootErrorCode } from "@loot-os/core";
import {
  MAX_PAID_COST_CENTS,
  PAID_COST_PART_RULES,
} from "@loot-os/fulfillment-core/order/constants";
import {
  type ChargedLine,
  checkoutTotalCentsOf,
  type EnteredPaidCost,
  findPaidAboveCharged,
  itemCostCentsOf,
  type PaidAboveChargedLine,
  type PaidCostParts,
} from "@loot-os/fulfillment-core/order/paid-above-charged";
import type { LinePaidCost } from "@loot-os/fulfillment-core/order/stockx-size-refund";
import { TRPCClientError } from "@trpc/client";

/** One field of a StockX line's paid-cost form. */
export interface PaidCostField {
  key: keyof PaidCostParts;
  label: string;
  /** Blank reads as 0. From the part's rule in `PAID_COST_PART_RULES`. */
  isOptional: boolean;
  help?: string;
}

/** A form field for one part, required or optional as its rule says. */
function partField(key: keyof PaidCostParts, label: string, help?: string): PaidCostField {
  return {
    key,
    label,
    isOptional: PAID_COST_PART_RULES[key].isOptional,
    ...(help ? { help } : {}),
  };
}

/**
 * The fields of a StockX line's paid-cost form, in the order the StockX order details page lists
 * them, so staff type what they see (Kai 2026-10-28). Additional charges comes after Shipping, for
 * a line a non-US page shows that the form has no field for.
 */
export const PAID_COST_FIELDS: readonly PaidCostField[] = [
  partField("purchasePriceCents", "Your Purchase Price"),
  partField("salesTaxCents", "Sales Tax"),
  partField("marketplaceFeeCents", "Marketplace Fee"),
  partField("shippingCents", "Shipping"),
  partField(
    "additionalChargesCents",
    "Additional charges",
    "Any other charge on the StockX order, such as VAT or import fees",
  ),
];

/** What staff typed in one line's fields, in dollars, by part. */
export type PaidCostDollars = Partial<Record<keyof PaidCostParts, string>>;

/**
 * One line's typed amounts as the parts the transition takes, or null while a required field is
 * blank or any field is not an amount the transition accepts: each part from its rule's minimum
 * (`PAID_COST_PART_RULES`), and an item cost no larger than the `integer` column's top.
 */
export function readPaidCostParts(dollars: PaidCostDollars): PaidCostParts | null {
  const parts: Partial<Record<keyof PaidCostParts, number>> = {};
  for (const field of PAID_COST_FIELDS) {
    const typed = dollars[field.key]?.trim() ?? "";
    if (typed === "" && field.isOptional) continue;
    const cents = parseAmount(typed, USD);
    const { minCents } = PAID_COST_PART_RULES[field.key];
    if (cents === null || cents < minCents || cents > MAX_PAID_COST_CENTS) return null;
    parts[field.key] = cents;
  }
  const { purchasePriceCents, marketplaceFeeCents, shippingCents } = parts;
  if (
    purchasePriceCents === undefined ||
    marketplaceFeeCents === undefined ||
    shippingCents === undefined
  ) {
    return null;
  }
  const read: PaidCostParts = { ...parts, purchasePriceCents, marketplaceFeeCents, shippingCents };
  return itemCostCentsOf(read) > MAX_PAID_COST_CENTS ? null : read;
}

/** The total the typed fields add up to, for staff to compare with StockX's Total; null until they read. */
export function typedCheckoutTotalCents(dollars: PaidCostDollars): number | null {
  const parts = readPaidCostParts(dollars);
  return parts ? checkoutTotalCentsOf(parts) : null;
}

/**
 * One paid cost per StockX line from the amounts typed so far, or null while any line's fields do
 * not read ({@link readPaidCostParts}). The transition refuses a move to PROCESSING without every
 * one (ENG-7343), so the form asks for them first.
 */
export function buildPaidCosts(
  stockxLineIds: readonly string[],
  dollarsByLineId: Readonly<Record<string, PaidCostDollars>>,
): LinePaidCost[] | null {
  const paidCosts: LinePaidCost[] = [];
  for (const lineId of stockxLineIds) {
    const parts = readPaidCostParts(dollarsByLineId[lineId] ?? {});
    if (parts === null) return null;
    paidCosts.push({ lineId, parts });
  }
  return paidCosts;
}

/** A StockX line whose entered paid cost is above its limit, as the warning shows it. */
export interface PaidAboveChargedRow extends PaidAboveChargedLine {
  /** What Loot Labs pays on top: the compared cost less the limit. */
  differenceCents: number;
}

/** What the check reads of one sent paid cost: a single amount is the item cost with no shipping. */
function enteredPaidCostOf({ paidCostCents, parts }: LinePaidCost): EnteredPaidCost {
  return parts
    ? { itemCostCents: itemCostCentsOf(parts), shippingCents: parts.shippingCents }
    : { itemCostCents: paidCostCents ?? 0, shippingCents: 0 };
}

/**
 * The lines whose paid cost is above their limit, by the rule the move to PROCESSING refuses them
 * with (`findPaidAboveCharged`), with what Loot Labs would pay on top. Empty when every line is at
 * or below its limit.
 */
export function paidAboveCharged(
  lines: readonly ChargedLine[],
  paidCosts: readonly LinePaidCost[],
): PaidAboveChargedRow[] {
  const enteredByLineId = new Map(
    paidCosts.map((paidCost) => [paidCost.lineId, enteredPaidCostOf(paidCost)]),
  );
  return findPaidAboveCharged(lines, enteredByLineId).map((line) => ({
    ...line,
    differenceCents: line.comparedCostCents - line.limitCents,
  }));
}

/** The Loot code of the move to PROCESSING's refusal of an unconfirmed paid cost above the charged price. */
const PAID_ABOVE_CHARGED_APP_CODE = "paid_cost_above_charged" satisfies LootErrorCode;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The whole-cent fields of one refused line. */
const REFUSED_LINE_CENTS = [
  "chargedPriceCents",
  "chargedShippingSurchargeCents",
  "itemCostCents",
  "shippingCents",
  "comparedCostCents",
  "limitCents",
] as const;

/** Whether a refusal amount is a whole, non-negative number of cents. */
function isWholeCents(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/** One `details.lines` entry of the refusal, or null when a field is missing or mistyped. */
function parseRefusedLine(value: unknown): PaidAboveChargedRow | null {
  if (!isRecord(value)) return null;
  const { lineId, countsShipping } = value;
  if (typeof lineId !== "string" || typeof countsShipping !== "boolean") return null;
  const cents: Partial<Record<(typeof REFUSED_LINE_CENTS)[number], number>> = {};
  for (const key of REFUSED_LINE_CENTS) {
    const amount = value[key];
    if (!isWholeCents(amount)) return null;
    cents[key] = amount;
  }
  // A server deployed before it sent the US shipping held no US shipping in the limit.
  const { chargedUsShippingCents = 0 } = value;
  if (!isWholeCents(chargedUsShippingCents)) return null;
  const line = { lineId, countsShipping, chargedUsShippingCents, ...cents } as PaidAboveChargedLine;
  return { ...line, differenceCents: line.comparedCostCents - line.limitCents };
}

/**
 * The lines the server named when it refused the move to PROCESSING for a paid cost above the
 * limit (`details.lines` of `paid_cost_above_charged`), as the warning shows them. The server
 * reads the charged price from its own copy of the snapshot, so it can refuse costs the page's
 * check passed. Null for any other error, or when the lines do not read, which leaves the query
 * client's toast to report it.
 */
export function readPaidAboveChargedRefusal(error: unknown): PaidAboveChargedRow[] | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data: unknown = error.data;
  if (!isRecord(data) || data.appCode !== PAID_ABOVE_CHARGED_APP_CODE) return null;
  const lines = isRecord(data.details) ? data.details.lines : undefined;
  if (!Array.isArray(lines) || lines.length === 0) return null;
  const rows = lines.map(parseRefusedLine);
  return rows.every((row): row is PaidAboveChargedRow => row !== null) ? rows : null;
}
